import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import { ArrowBack as BackIcon, Build as ProductionIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useVenue } from '../../../../context/VenueContext';
import productionOrderService from '../../../../services/productionOrderService';
import recipeService from '../../../../services/recipeService';
import venueLocationService from '../../../../services/venueLocationService';
import ownershipAccountService from '../../../../services/ownershipAccountService';
import type { Recipe, RecipeLine, VenueLocation, OwnershipAccount } from '../../../../types/venue';

const ProductionOrderCreate: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { venueId } = useVenue();
  const backPath = `/admin/venues/${venueId}/production-orders`;

  // ── Reference data ─────────────────────────────────────────────────────────
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [locations, setLocations] = useState<VenueLocation[]>([]);
  const [accounts, setAccounts] = useState<OwnershipAccount[]>([]);
  const [refLoading, setRefLoading] = useState(true);

  useEffect(() => {
    if (!venueId) return;
    Promise.all([
      recipeService.listRecipes(venueId, { active: true, pageSize: 200 }),
      venueLocationService.getLocations(venueId),
      ownershipAccountService.listAccounts(venueId, { active: true }),
    ]).then(([recipeRes, locs, accts]) => {
      setRecipes(recipeRes.data);
      setLocations(locs.filter(l => l.active));
      setAccounts(accts.data);
    }).catch(() => {}).finally(() => setRefLoading(false));
  }, [venueId]);

  // ── Form state ─────────────────────────────────────────────────────────────
  const [selectedRecipe, setSelectedRecipe] = useState<Recipe | null>(null);
  const [recipeLines, setRecipeLines] = useState<RecipeLine[]>([]);
  const [recipeLineLoading, setRecipeLineLoading] = useState(false);
  const [plannedQuantity, setPlannedQuantity] = useState<string>('1');
  const [locationId, setLocationId] = useState<string>('');
  const [outputOwnershipAccountId, setOutputOwnershipAccountId] = useState<string>('');
  const [plannedDate, setPlannedDate] = useState<string>('');
  const [outputExpiryDate, setOutputExpiryDate] = useState<string>('');
  const [outputLotNumber, setOutputLotNumber] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Pre-select default location and account when reference data loads
  useEffect(() => {
    const defaultLoc = locations.find(l => l.isDefault) ?? locations[0];
    if (defaultLoc && !locationId) setLocationId(defaultLoc._id);
  }, [locations]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const defaultAcct = accounts.find(a => a.isVenueDefault) ?? accounts[0];
    if (defaultAcct && !outputOwnershipAccountId) setOutputOwnershipAccountId(defaultAcct._id);
  }, [accounts]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load recipe lines preview when recipe selected
  useEffect(() => {
    if (!selectedRecipe) { setRecipeLines([]); return; }
    setRecipeLineLoading(true);
    recipeService.listRecipeLines(venueId, selectedRecipe._id)
      .then(r => setRecipeLines(r.lines))
      .catch(() => setRecipeLines([]))
      .finally(() => setRecipeLineLoading(false));
  }, [selectedRecipe, venueId]);

  // ── Submit ──────────────────────────────────────────────────────────────────
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedRecipe) { setError('Please select a recipe.'); return; }
    const qty = parseFloat(plannedQuantity);
    if (!qty || qty <= 0) { setError('Planned quantity must be greater than 0.'); return; }
    if (!locationId) { setError('Please select an output location.'); return; }
    if (!outputOwnershipAccountId) { setError('Please select an output ownership account.'); return; }

    setSaving(true);
    setError(null);
    try {
      const result = await productionOrderService.createProductionOrder(venueId, {
        recipeId: selectedRecipe._id,
        plannedQuantity: qty,
        locationId,
        outputOwnershipAccountId,
        plannedDate: plannedDate || null,
        outputExpiryDate: outputExpiryDate || null,
        outputLotNumber: outputLotNumber || null,
        notes: notes || null,
      });
      navigate(`${backPath}/${result.order._id}`);
    } catch (err: any) {
      setError(err?.response?.data?.error?.message ?? err?.message ?? 'Failed to create production order');
    } finally {
      setSaving(false);
    }
  };

  // ── Derived preview ─────────────────────────────────────────────────────────
  const previewLines = useMemo(() => {
    const qty = parseFloat(plannedQuantity) || 1;
    return recipeLines.map(line => ({
      ...line,
      previewRequired: line.quantity * qty,
    }));
  }, [recipeLines, plannedQuantity]);

  if (refLoading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', pt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      {/* Nav */}
      <Box>
        <Button
          startIcon={<BackIcon />}
          onClick={() => navigate(backPath)}
          sx={{ textTransform: 'none', color: 'text.secondary' }}
        >
          Production Orders
        </Button>
      </Box>

      <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        {/* Header */}
        <Paper sx={{ p: 3, borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
            <ProductionIcon sx={{ color: theme.palette.primary.main }} />
            <Typography variant="h6" fontWeight={600}>New Production Order</Typography>
          </Box>

          {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</Alert>}

          <Alert severity="info" sx={{ mb: 2.5, py: 0.5, fontSize: '0.78rem' }} icon={false}>
            <strong>Production orders consume ingredients and post manufactured output to your chosen location.</strong>{' '}
            They are not purchase orders — to order from a supplier, use <em>Purchasing → Purchase Orders</em>.
            If a MANUFACTURED item shows a Low Stock alert, schedule a production run here instead of reordering.
          </Alert>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            {/* Recipe selector */}
            <Autocomplete<Recipe>
              options={recipes}
              getOptionLabel={r => r.name}
              value={selectedRecipe}
              onChange={(_, v) => setSelectedRecipe(v)}
              renderInput={params => (
                <TextField
                  {...params}
                  label="Recipe *"
                  size="small"
                  placeholder="Search active recipes…"
                />
              )}
              isOptionEqualToValue={(opt, val) => opt._id === val._id}
              noOptionsText="No active recipes found"
            />

            {/* Selected recipe info */}
            {selectedRecipe && (
              <Box sx={{ px: 2, py: 1.5, bgcolor: alpha(theme.palette.primary.main, 0.04), borderRadius: 1.5, border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}` }}>
                <Typography variant="caption" color="text.secondary">Recipe: </Typography>
                <Typography variant="caption" fontWeight={500}>{selectedRecipe.name}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                  Batch qty: {selectedRecipe.outputQuantity} · v{selectedRecipe.version}
                </Typography>
              </Box>
            )}

            {/* Core fields */}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <TextField
                label="Planned Quantity *"
                size="small"
                type="number"
                value={plannedQuantity}
                onChange={e => setPlannedQuantity(e.target.value)}
                inputProps={{ min: 0.001, step: 'any' }}
                sx={{ width: 180 }}
              />

              <FormControl size="small" sx={{ minWidth: 220 }} required>
                <InputLabel>Output Location *</InputLabel>
                <Select
                  label="Output Location *"
                  value={locationId}
                  onChange={e => setLocationId(e.target.value)}
                >
                  {locations.map(l => (
                    <MenuItem key={l._id} value={l._id}>
                      {l.displayName}{l.isDefault ? ' (default)' : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 220 }} required>
                <InputLabel>Output Ownership *</InputLabel>
                <Select
                  label="Output Ownership *"
                  value={outputOwnershipAccountId}
                  onChange={e => setOutputOwnershipAccountId(e.target.value)}
                >
                  {accounts.map(a => (
                    <MenuItem key={a._id} value={a._id}>
                      {a.name}{a.isVenueDefault ? ' (default)' : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Optional fields */}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <TextField
                label="Planned Date"
                size="small"
                type="date"
                value={plannedDate}
                onChange={e => setPlannedDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ width: 200 }}
              />
              <TextField
                label="Output Expiry Date"
                size="small"
                type="date"
                value={outputExpiryDate}
                onChange={e => setOutputExpiryDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ width: 200 }}
              />
              <TextField
                label="Output Lot Number"
                size="small"
                value={outputLotNumber}
                onChange={e => setOutputLotNumber(e.target.value)}
                placeholder="Auto-generated if blank"
                sx={{ flex: 1, minWidth: 200 }}
              />
            </Box>

            <TextField
              label="Notes"
              size="small"
              multiline
              rows={2}
              value={notes}
              onChange={e => setNotes(e.target.value)}
              fullWidth
            />
          </Box>
        </Paper>

        {/* Ingredients preview */}
        {selectedRecipe && (
          <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
            <Box sx={{ px: 3, py: 2, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="subtitle1" fontWeight={600}>Ingredient Preview</Typography>
              {recipeLineLoading && <CircularProgress size={16} />}
              <Typography variant="caption" color="text.secondary">
                Based on ×{parseFloat(plannedQuantity) || 1} planned quantity
              </Typography>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {['Ingredient', 'Per Batch', 'Required (×qty)'].map(h => (
                      <TableCell
                        key={h}
                        sx={{ fontWeight: 600, color: theme.palette.text.secondary, fontSize: '0.7rem', textTransform: 'uppercase', py: 1.25 }}
                      >
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recipeLines.length === 0 && !recipeLineLoading ? (
                    <TableRow>
                      <TableCell colSpan={3}>
                        <Box sx={{ py: 3, textAlign: 'center', color: theme.palette.text.secondary }}>
                          <Typography variant="body2">This recipe has no ingredient lines.</Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    previewLines.map(line => (
                      <TableRow key={line._id}>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>
                            {line.displayName ?? line.inventoryItemId}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {line.quantity}{line.sizeUnit ? ` ${line.sizeUnit}` : ''}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {line.previewRequired.toFixed(3).replace(/\.?0+$/, '')}{line.sizeUnit ? ` ${line.sizeUnit}` : ''}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}

        {/* Actions */}
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Button
            variant="outlined"
            onClick={() => navigate(backPath)}
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={saving || !selectedRecipe}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {saving ? 'Creating…' : 'Create Production Order'}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default ProductionOrderCreate;
